import { Accordion, AccordionItem, Skeleton } from '@heroui/react';
import { NavArrowRight } from 'iconoir-react';
import head from 'lodash/head';

import i18n from '~/common/helpers/i18n';
import { slugify } from '~/common/helpers/string';
import { CopyableText } from '~/components/CopyableText';
import { Deployment } from '~/entities/Deployment';
import { Trigger } from '~/entities/Trigger';

export type TriggerSourceItem = {
  label: string;
  value: string;
};

export type TriggerDataItem = {
  key: string;
  value: string;
};

type DeploymentTriggerSectionProps = {
  deployment: Deployment;
  trigger: Trigger;
  triggerSource?: TriggerSourceItem[];
};

/**
 * The trigger block: its icon and name, the pipeline description, the deployed endpoints and the
 * trigger configuration read off the trigger's own json schema.
 *
 * The endpoints sit in the panel itself — they are what people open this drawer to copy. The
 * configuration keeps the beehive behaviour and stays collapsed until asked for: it is a long list
 * of trigger internals, and nobody reads it by default.
 */
export const DeploymentTriggerSection = ({
  deployment,
  trigger,
  triggerSource = []
}: DeploymentTriggerSectionProps) => {
  const iconLabel = i18n.t('scenes.beta_pipelines.labels.trigger_icon') || '';

  if (!trigger) {
    return (
      <section
        className='rounded-lg border border-solid border-outline px-4 py-3.5'
        data-testid='run-page-detail'
      >
        <Skeleton className='h-11 w-11 rounded-full' />
      </section>
    );
  }

  // `data` is not on the Trigger entity, but it is what the deployment query returns for the
  // deployed endpoints — the beehive sidesheet read the same field through a ts-expect-error.
  const triggers = ((
    deployment?.trigger as unknown as { data?: TriggerDataItem[] }
  )?.data ?? []) as TriggerDataItem[];
  const schemaLabel = (
    head(trigger?.jsonSchema as unknown as { label?: string }[]) as
      { label?: string } | undefined
  )?.label;

  return (
    <>
      <section
        className='rounded-lg border border-solid border-outline px-4 py-3.5'
        data-testid='run-page-detail'
      >
        <div className='flex items-center gap-3'>
          <img
            src={trigger.iconURL}
            alt={iconLabel}
            title={iconLabel}
            className='h-10 w-10 shrink-0 select-none rounded-full border border-outline bg-surface'
          />
          <div className='min-w-0'>
            {schemaLabel && (
              <p className='truncate text-label-lg font-semibold text-on-surface'>
                {schemaLabel}
              </p>
            )}
            <p className='line-clamp-2 text-body-regular-sm text-on-surface-variant'>
              {deployment?.pipeline?.description}
            </p>
          </div>
        </div>

        {triggers.length > 0 && (
          <div className='mt-3.5 flex flex-col gap-2 border-t border-outline pt-3.5'>
            {triggers.map(item => (
              <div
                key={`${item.key}-${item.value}`}
                className='flex min-w-0 flex-col gap-0.5 rounded-md bg-surface-dim px-3 py-2'
              >
                <span className='text-label-md text-on-surface-variant'>
                  {item.key}
                </span>
                {/* `whitespace-normal break-all` on the CopyableText itself, the way the Monitor
                    log cells pass it: the HeroUI Button underneath is `whitespace-nowrap min-w-max`,
                    so the same classes on the inner span lose and the endpoint gets cut off
                    mid-URL. `break-all` because an endpoint has no spaces to wrap on. */}
                <CopyableText
                  text={item.value}
                  data-testid={`run-detail-endpoint-copy-${slugify(item.key)}`}
                  className='group min-w-0 justify-start whitespace-normal break-all text-left font-mono text-body-regular-sm text-on-surface'
                >
                  <span>{item.value}</span>
                </CopyableText>
              </div>
            ))}
          </div>
        )}
      </section>

      {triggerSource.length > 0 && (
        <Accordion
          className='px-0'
          selectionMode='single'
          itemClasses={{
            // The design draws this as its own card: a 6px radius over the surface, and the header
            // row 44px tall with 14px of padding on the label side and 8px on the chevron's.
            base: 'overflow-hidden rounded-md border border-solid border-outline bg-surface',
            trigger:
              'min-h-11 items-center bg-transparent py-2.5 pl-3.5 pr-2 data-[hover=true]:bg-transparent data-[open=true]:border-b data-[open=true]:border-outline',
            titleWrapper: 'px-0',
            // A 24px box around the chevron, and the rotation flipped: HeroUI turns its indicator
            // anticlockwise on open, which would point a right-facing chevron up instead of down.
            indicator:
              'flex size-6 items-center justify-center text-on-surface data-[open=true]:rotate-90',
            content: 'gap-3 px-3.5 pb-3.5 pt-2.5'
          }}
        >
          <AccordionItem
            key='trigger-settings'
            aria-label={i18n.t('label.settings') || ''}
            // Iconoir instead of HeroUI's own `ChevronIcon`, and one icon rather than two: closed
            // it points right, as the design has it, and the indicator slot turns it down on open.
            indicator={<NavArrowRight className='icon-small' />}
            // The trigger's own name heads the panel above; repeating it here said nothing.
            // The test id rides the title and not the `AccordionItem`, whose extra props land on
            // the wrapper instead of the button that opens it.
            title={
              <span
                className='text-title-sm text-on-surface'
                data-testid='run-detail-trigger-settings-toggle'
              >
                {i18n.t('label.settings')}
              </span>
            }
          >
            {/* Striped: one-word keys against long JSON values, and without the banding the eye
                loses the line halfway across. */}
            <dl className='overflow-hidden rounded-md'>
              {triggerSource.map(item => (
                <div
                  key={`${item.label}-${item.value}`}
                  className='flex gap-3 px-3 py-2 odd:bg-surface-dim'
                  title={item.value}
                >
                  <dt className='w-2/5 shrink-0 truncate text-body-regular-sm text-on-surface-variant'>
                    {item.label}
                  </dt>
                  <dd className='min-w-0 flex-1 truncate font-mono text-body-regular-sm text-on-surface'>
                    {item.value}
                  </dd>
                </div>
              ))}
            </dl>
          </AccordionItem>
        </Accordion>
      )}
    </>
  );
};

export default DeploymentTriggerSection;
